import {
  forwardRef,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "../lib/cn.ts";
import { IconChevronDown, IconWarning } from "./icons.tsx";

/**
 * Primitivos de formulário e superfície.
 *
 * Medidas e cores do Ofative (Guideline + Login). A estrutura, os estados e a
 * semântica de acessibilidade estão completos.
 */

const FIELD_BASE =
  "w-full bg-[var(--color-surface)] text-[var(--color-ink)] border border-[var(--color-line-strong)] " +
  "rounded-[var(--radius-md)] px-3.5 text-[14px] font-medium tracking-[0] " +
  "transition-[border-color,box-shadow] " +
  "duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)] " +
  "placeholder:text-[var(--color-ink-subtle)] placeholder:font-medium " +
  "hover:border-[var(--color-ink-subtle)] " +
  "focus:outline-none focus:border-brand-500 focus:shadow-[var(--dream-focus-ring)] " +
  "disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-ink-muted)] " +
  "disabled:cursor-not-allowed";

const FIELD_INVALID =
  "border-[#F44A4A] hover:border-[#AB3434] focus:border-[#F44A4A] " +
  "focus:shadow-[0_0_0_3px_rgb(244_74_74_/_0.28)]";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(FIELD_BASE, "h-11", invalid && FIELD_INVALID, className)}
      {...rest}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, rows = 3, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        FIELD_BASE,
        "py-2 resize-y min-h-[4.5rem]",
        invalid && FIELD_INVALID,
        className,
      )}
      {...rest}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

/**
 * Select nativo.
 *
 * Deliberadamente nativo em vez do Radix Select: em mobile o seletor do sistema
 * é mais rápido e acessível do que qualquer reimplementação, e o briefing pede
 * Radix "somente quando ajudarem em acessibilidade, sem impor aparência
 * própria". Aqui não ajudaria.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid = false, children, ...rest },
  ref,
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          FIELD_BASE,
          "h-10 pr-9 appearance-none cursor-pointer",
          invalid && FIELD_INVALID,
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <IconChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-[var(--color-ink-muted)]"
      />
    </div>
  );
});

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
  /**
   * Id do controle associado.
   *
   * Declarado explicitamente em vez de trafegar por `...rest`: um rótulo sem
   * associação não é anunciado com o campo por leitores de tela, e passar o
   * atributo por spread esconde essa exigência de quem consome o componente.
   */
  htmlFor: string;
}

export function Label({ className, required = false, children, htmlFor, ...rest }: LabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        "mb-1.5 block text-[14px] font-medium text-[var(--color-ink-secondary)]",
        className,
      )}
      {...rest}
    >
      {children}
      {required && (
        <span className="text-[#F44A4A] ml-0.5" aria-hidden="true">
          *
        </span>
      )}
    </label>
  );
}

export interface FieldProps {
  label: string;
  /**
   * Obrigatório: é o que liga o rótulo ao controle.
   *
   * Sem a associação, um leitor de tela anuncia o campo sem dizer o que ele é.
   * Exigir no tipo torna impossível esquecer.
   */
  htmlFor: string;
  required?: boolean;
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
  className?: string;
}

/**
 * Agrupa rótulo, controle, dica e erro.
 *
 * A mensagem de erro usa `role="alert"`, para que leitores de tela a anunciem
 * assim que a validação falha, em vez de o usuário descobrir só ao navegar.
 */
export function Field({
  label,
  htmlFor,
  required = false,
  error,
  hint,
  children,
  className,
}: FieldProps) {
  return (
    <div className={cn("min-w-0", className)}>
      <Label htmlFor={htmlFor} required={required}>
        {label}
      </Label>
      {children}
      {hint !== undefined && error === undefined && (
        <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{hint}</p>
      )}
      {error !== undefined && (
        <p role="alert" className="mt-1 text-xs font-medium text-[#AB3434]">
          {error}
        </p>
      )}
    </div>
  );
}

export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "bg-[var(--color-surface)] border border-[var(--color-line)] rounded-[var(--radius-lg)] shadow-xs",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--color-line)]",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardTitle({ className, children, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn("text-[0.9375rem] font-semibold text-[var(--color-ink)]", className)}
      {...rest}
    >
      {children}
    </h2>
  );
}

export function Skeleton({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div aria-hidden="true" className={cn("dream-skeleton h-4 w-full", className)} {...rest} />
  );
}

export interface EmptyStateProps {
  title: string;
  description: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn("dream-enter flex flex-row items-start gap-3 px-6 py-14 text-left", className)}
    >
      {icon !== undefined && (
        <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
          {icon}
        </div>
      )}
      <div className="min-w-0">
        <h3 className="text-[0.9375rem] font-bold tracking-[0] text-[var(--color-ink)]">{title}</h3>
        <p className="mt-1 max-w-md text-[0.8125rem] leading-5 text-[var(--color-ink-secondary)]">
          {description}
        </p>
        {action !== undefined && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  className?: string;
}

/**
 * Estado de erro.
 *
 * Mostra a mensagem real em vez de "algo deu errado": o briefing proíbe
 * esconder erros, e uma mensagem genérica impede o usuário de agir.
 */
export function ErrorState({
  title = "Não foi possível carregar",
  message,
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn("dream-enter flex flex-row items-start gap-3 px-6 py-12 text-left", className)}
    >
      <div className="mb-0 flex size-11 shrink-0 items-center justify-center rounded-2xl bg-[#FEEDED] text-[#F44A4A]">
        <IconWarning className="size-5" />
      </div>
      <div className="min-w-0">
        <h3 className="text-[0.9375rem] font-bold tracking-[0] text-[var(--color-ink)]">{title}</h3>
        <p className="mt-1 max-w-md text-[0.8125rem] leading-5 text-[var(--color-ink-secondary)]">
          {message}
        </p>
        {onRetry !== undefined && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 h-9 rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-4 text-[0.8125rem] font-medium transition-[background-color] duration-[var(--dream-duration-fast)] hover:bg-[var(--color-surface-subtle)]"
          >
            Tentar novamente
          </button>
        )}
      </div>
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cn("border-0 border-t border-[var(--color-line)]", className)} />;
}

/** Anúncio para leitores de tela, sem impacto visual. */
export function LiveRegion({ message }: { message: string }) {
  return (
    <div role="status" aria-live="polite" className="sr-only">
      {message}
    </div>
  );
}
